/** Offset and length of a token inside a sentence. */
export type WordSpan = [offset: number, length: number]
export type WordArticle = 'a' | 'an' | 'the' | 'none'

const WORD_PATTERN = /\p{L}+(?:'\p{L}+)?/gu
const MAX_RELATIVE_DISTANCE = 0.6

/**
 * Locates a term inside its own usage example.
 *
 * The model returns inflected forms ("running" for the lemma "run"), so an exact
 * match is not enough. Returns undefined when no token is close enough, letting
 * the caller keep the sense without highlight instead of failing the whole word.
 */
export function findTermSpan(
  term: string,
  sentence: string,
): WordSpan | undefined {
  const needle = term.toLowerCase()
  const haystack = sentence.toLowerCase()

  let best: { span: WordSpan; distance: number } | undefined

  for (const match of haystack.matchAll(WORD_PATTERN)) {
    const token = match[0]
    const offset = match.index
    const distance =
      levenshtein(needle, token) / Math.max(needle.length, token.length)
    if (
      !best ||
      distance < best.distance ||
      (distance === best.distance && token.length > best.span[1])
    ) {
      best = { span: [offset, token.length], distance }
    }
  }

  if (!best || best.distance > MAX_RELATIVE_DISTANCE) return undefined
  return best.span
}

/**
 * Locates the article that governs the term in an article example sentence.
 *
 * Only the text before the term is searched, so "a" in "a cat sat on a mat"
 * resolves to the article of the term, not to a later unrelated one.
 */
export function findArticleSpan(
  sentence: string,
  article: WordArticle,
  termOffset: number,
): WordSpan | undefined {
  if (article === 'none') return undefined

  const prefix = sentence.slice(0, Math.max(termOffset, 0)).toLowerCase()
  const pattern = new RegExp(`\\b${article}\\b`, 'g')

  let found: number | undefined
  for (const match of prefix.matchAll(pattern)) found = match.index

  if (found === undefined) return undefined
  return [found, article.length]
}

export function levenshtein(left: string, right: string): number {
  if (left === right) return 0
  if (left.length === 0) return right.length
  if (right.length === 0) return left.length

  let previous = Array.from({ length: right.length + 1 }, (_, index) => index)

  for (let row = 1; row <= left.length; row++) {
    const current = [row]
    for (let column = 1; column <= right.length; column++) {
      const substitution =
        (previous[column - 1] ?? 0) +
        (left[row - 1] === right[column - 1] ? 0 : 1)
      const deletion = (previous[column] ?? 0) + 1
      const insertion = (current[column - 1] ?? 0) + 1
      current[column] = Math.min(substitution, deletion, insertion)
    }
    previous = current
  }

  return previous[right.length] ?? 0
}
